import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router';
import {
  LogOut,
  ArrowLeftRight,
  ChevronDown,
  Sun,
  Moon,
  Monitor,
  Loader2,
  Copy,
  Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client/react';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { useThemeStore } from '@/stores/theme-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { setScioVersionCookie, isBehindCfWorker } from '@/lib/sso-cookies';
import { SCIO_SWITCH_LOGS, ME_QUERY, type MeQueryResult } from '@scio3/graphql-client';
import { fullLogout } from '@/lib/logout';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { showLoadingOverlay, dismissLoadingOverlay } from '@/components/shared';
import { TeamSwitcher } from './TeamSwitcher';
import { LanguageSwitcher } from './LanguageSwitcher';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@scio3/ui';

function getInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const THEME_OPTIONS = [
  { value: 'light', icon: Sun, label: 'Light' },
  { value: 'dark', icon: Moon, label: 'Dark' },
  { value: 'system', icon: Monitor, label: 'System' },
] as const;

interface UserMenuProps {
  compact?: boolean;
}

export function UserMenu({ compact }: UserMenuProps) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const activeTeam = useTeamStore((s) => s.getActiveTeam());
  const navigate = useNavigate();
  const client = useApolloClient();
  const { isRootAdmin, isRootSuperAdmin, isTeamAdmin } = usePermissions();
  const features = useCompanyFeatures();

  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const isWhiteLabel = !!me?.brandingData?.isWhiteLabel;

  const isDefaultTeam = useTeamStore((s) => s.isDefaultTeam);
  const [switchingToClassic, setSwitchingToClassic] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  async function handleSwitchToClassic() {
    if (switchingToClassic) return;
    setSwitchingToClassic(true);
    showLoadingOverlay();

    const teamId = useTeamStore.getState().activeTeamId;
    // 1. Mutation sets user.scioVersion=1 in MongoDB — abort if it fails,
    //    otherwise scio1 reads stale scioVersion=3 → cookie loop.
    try {
      await client.mutate({ mutation: SCIO_SWITCH_LOGS, variables: { teamId } });
    } catch {
      console.error('Failed to switch to classic — mutation failed');
      setSwitchingToClassic(false);
      return;
    }

    // 2. Verify the write is readable — re-query ME until scioVersion≠3.
    //    This prevents scio1's initSCIO20() from reading stale scioVersion=3
    //    (MongoDB replication lag) and overwriting cookie back to 3 → infinite loop.
    for (let i = 0; i < 5; i++) {
      const { data } = await client
        .query<MeQueryResult>({
          query: ME_QUERY,
          fetchPolicy: 'network-only',
        })
        .catch(() => ({ data: null }));
      if (!data?.me?.scioVersion || data.me.scioVersion !== 3) break;
      await new Promise((r) => setTimeout(r, 300));
    }

    setScioVersionCookie('1');
    // Redirect to scio1 with current path + search params preserved.
    const url = new URL(window.location.href);
    if (!isBehindCfWorker()) {
      const scio1 = new URL(import.meta.env.VITE_SCIO1_URL);
      url.href = new URL(url.pathname + url.search, scio1.origin).href;
    }
    // Skip activeTeamId for designManagement — scio1's designManagement.sendAuth()
    // triggers a team-switch reload when $stateParams.teamId differs from
    // the current Meteor team, which causes window.location.reload() that
    // loses all query params (e.g. asset_id). The team is already synced
    // via the scioSwitchLogs mutation above.
    // Use activeTeamId (not teamId) to avoid collision with teamId which is
    // folder-level context for cross-team shared folders (#459).
    const isDesigner = window.location.pathname.endsWith('/designManagement');
    if (!isDesigner) {
      url.searchParams.set('activeTeamId', teamId || '1');
    }
    // scio1's engage page needs tilde-encoded slashes (/ → ~2F) in the path param.
    // Angular UI Router uses ~2F encoding; standard %2F doesn't work for engage (#348).
    const pathParam = url.searchParams.get('path');
    if (pathParam && url.pathname.endsWith('/engageManagement')) {
      url.searchParams.set('path', pathParam.replace(/\//g, '~2F'));
    }
    window.location.href = url.toString();

    // If the user cancels the browser's beforeunload dialog, the page stays alive.
    // After 500ms, if we're still here, dismiss the loading overlay.
    setTimeout(() => {
      setSwitchingToClassic(false);
      dismissLoadingOverlay();
    }, 500);
  }

  async function handleLogout() {
    await fullLogout(client);
    navigate('/signIn');
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={compact ? 'icon-sm' : 'sm'}
          className={cn(
            'rounded-full text-muted-foreground hover:text-foreground hover:bg-sidebar-active',
            compact ? 'h-10 w-10' : 'h-auto gap-1.5 py-1 pl-1 pr-3',
          )}
          aria-label={t('User menu')}
          data-testid="user-menu"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {getInitials(me?.name)}
          </span>
          {!compact && (
            <>
              <span className="min-w-0 max-w-[120px] truncate text-sm font-medium">
                {activeTeam?.name === 'Default'
                  ? t('Default')
                  : activeTeam?.name?.trim() || t('Account')}
              </span>
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {/* User info header */}
        <div className="px-3 py-2">
          <p className="text-sm font-semibold text-foreground">{me?.name}</p>
          {me?.email && (
            <div
              className="group/copy relative cursor-pointer select-none"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(me.email ?? '');
                setEmailCopied(true);
                setTimeout(() => setEmailCopied(false), 1500);
              }}
            >
              <p className="text-xs text-muted-foreground truncate group-hover/copy:text-foreground transition-colors">
                {me.email}
              </p>
              <span className="absolute right-0 top-0 bottom-0 items-center hidden group-hover/copy:flex bg-gradient-to-l from-popover from-50% to-transparent pl-5">
                {emailCopied ? (
                  <Check className="h-3 w-3 text-success" />
                ) : (
                  <Copy className="h-3 w-3 text-muted-foreground" />
                )}
              </span>
            </div>
          )}
          {/* Inline theme toggle */}
          <div className="mt-2" onClick={(e) => e.stopPropagation()}>
            <ViewToggle
              value={theme}
              onValueChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}
              options={THEME_OPTIONS.map(({ value, icon, label }) => ({
                value,
                icon,
                label: t(label),
              }))}
            />
          </div>
        </div>
        <TeamSwitcher />
        {/* Main items */}
        <DropdownMenuItem onClick={() => navigate('/app/s/personal-profile')}>
          {t('Personal Profile')}
        </DropdownMenuItem>
        {isRootAdmin && !isWhiteLabel && (
          <DropdownMenuItem onClick={() => navigate('/app/s/company-profile')}>
            {t('Company Settings')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate('/app/s/account-members')}>
          {t('Account Members')}
        </DropdownMenuItem>
        {isRootAdmin && (
          <>
            <DropdownMenuItem onClick={() => navigate('/app/s/subscription-plan')}>
              {t('Subscription Plan')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/app/s/payment')}>
              {t('Payment Settings')}
            </DropdownMenuItem>
          </>
        )}
        {isRootAdmin && isDefaultTeam && (
          <DropdownMenuItem onClick={() => navigate('/app/s/order-device')}>
            {t('Order Devices')}
          </DropdownMenuItem>
        )}
        {isRootSuperAdmin && !isWhiteLabel && (
          <DropdownMenuItem onClick={() => navigate('/app/s/preference-settings')}>
            {t('Preferences')}
          </DropdownMenuItem>
        )}

        {/* More submenu - show for team admins and above */}
        {(isRootAdmin || isTeamAdmin) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>{t('More')}</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-56">
                {/* ROOT admin items (matches scio1 isRootAdmin) */}
                {isRootAdmin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/app/s/operational-schedule')}>
                      {t('Operational Schedule')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/s/payment-history')}>
                      {t('Billing History')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/s/sub-account-billing')}>
                      {t('Sub-Account Billing')}
                    </DropdownMenuItem>
                    {/* <DropdownMenuItem onClick={() => navigate('/app/s/coupons')}>
                      {t('Coupons')}
                    </DropdownMenuItem> */}
                    <DropdownMenuItem onClick={() => navigate('/app/s/ordered-device')}>
                      {t('Order History')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/s/proof-of-play-settings')}>
                      {t('Proof of Play')}
                    </DropdownMenuItem>
                  </>
                )}

                {/* Branding - ROOT admin (owner, admin, super admin) */}
                {isRootAdmin && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/branding-settings')}>
                    {t('Branding')}
                  </DropdownMenuItem>
                )}

                {/* Advanced admin items - ROOT admin only */}
                {isRootAdmin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/app/security-audit-logging')}>
                      {t('Audit Log')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/app/s/monitoring-alert-rules')}>
                      {t('Monitoring Alert')}
                    </DropdownMenuItem>
                    {/* SAML & API Keys hidden for white-label (matches scio1) */}
                    {!isWhiteLabel && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/app/s/saml-settings')}>
                          {t('SAML Single Sign On')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/app/s/apikeys')}>
                          {t('API Keys')}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onClick={() => navigate('/app/s/external-coms')}>
                      {t('External Communications')}
                    </DropdownMenuItem>
                  </>
                )}

                {/* Team-level items visible to team admins+ (matches scio1 isTeamAdmin) */}
                <DropdownMenuItem onClick={() => navigate('/app/s/integrations')}>
                  {t('Integrations')}
                </DropdownMenuItem>
                {company?.feature?.shareLink && (
                  <DropdownMenuItem onClick={() => navigate('/app/s/share-links-admin')}>
                    {t('Share Links')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate('/app/s/datasources-admin')}>
                  {t('DataSources')}
                </DropdownMenuItem>
                {features.smallappManifest && (
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => navigate('/app/dev/smallapp-playground')}
                  >
                    {t('SmallApp Playground')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}

        {/* Language */}
        <DropdownMenuSeparator />
        <LanguageSwitcher />

        {/* Switch to Classic & Logout */}
        <DropdownMenuSeparator />
        {(isBehindCfWorker()
          ? !(company?.scioVersion != null && company.scioVersion > 1)
          : !!import.meta.env.VITE_SCIO1_URL) && (
          <DropdownMenuItem onClick={handleSwitchToClassic} disabled={switchingToClassic}>
            {switchingToClassic ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowLeftRight className="mr-2 h-4 w-4" />
            )}
            {switchingToClassic ? t('Switching…') : t('Switch to Classic')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          {t('Log Out')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
